import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { AdminShell } from "./layouts/AdminShell";
import { AppShell } from "./layouts/AppShell";
import { StudioShell } from "./layouts/StudioShell";
import { AuthPage } from "./pages/AuthPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { TermsPage } from "./pages/TermsPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { INTEGRATIONS_ENABLED } from "./constants/features";
import { useAuth } from "./services/auth-context";
import { useSubscription } from "./services/subscription-context";

// Lazy-loaded pages — loaded on demand to reduce initial bundle size
const BillingPage = lazy(() => import("./pages/BillingPage").then(m => ({ default: m.BillingPage })));
const ClientsPage = lazy(() => import("./pages/ClientsPage").then(m => ({ default: m.ClientsPage })));
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const ConversationsPage = lazy(() => import("./pages/ConversationsPage").then(m => ({ default: m.ConversationsPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const InvoiceConversationsPage = lazy(() => import("./pages/InvoiceConversationsPage").then(m => ({ default: m.InvoiceConversationsPage })));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage").then(m => ({ default: m.InvoicesPage })));
const LogsPage = lazy(() => import("./pages/LogsPage").then(m => ({ default: m.LogsPage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const PlanPaymentPage = lazy(() => import("./pages/PlanPaymentPage").then(m => ({ default: m.PlanPaymentPage })));
const PlansPage = lazy(() => import("./pages/PlansPage").then(m => ({ default: m.PlansPage })));
const PoliciesPage = lazy(() => import("./pages/PoliciesPage").then(m => ({ default: m.PoliciesPage })));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const SettingsPrivacyPage = lazy(() => import("./pages/SettingsPrivacyPage").then(m => ({ default: m.SettingsPrivacyPage })));
const TemplatesPage = lazy(() => import("./pages/TemplatesPage").then(m => ({ default: m.TemplatesPage })));

// Studio (commercialisti) pages
const StudioDashboardPage = lazy(() => import("./pages/studio/StudioDashboardPage"));
const StudioInvitePage = lazy(() => import("./pages/studio/StudioInvitePage"));
const StudioSettingsPage = lazy(() => import("./pages/studio/StudioSettingsPage"));

// Admin pages
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminWorkspacesPage = lazy(() => import("./pages/admin/AdminWorkspacesPage"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminConversationsPage = lazy(() => import("./pages/admin/AdminConversationsPage"));
const AdminAuditPage = lazy(() => import("./pages/admin/AdminAuditPage"));
const AdminContactsPage = lazy(() => import("./pages/admin/AdminContactsPage"));
const AdminAppointmentsPage = lazy(() => import("./pages/admin/AdminAppointmentsPage"));
const AdminPlansPage = lazy(() => import("./pages/admin/AdminPlansPage"));
const AdminWorkspaceHealthPage = lazy(() => import("./pages/admin/AdminWorkspaceHealthPage"));

function PageLoader() {
  return (
    <section style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
      <p>Caricamento...</p>
    </section>
  );
}

function BillingLoadingScreen({ message }: { message: string }) {
  return (
    <section className="auth-screen">
      <div className="auth-panel">
        <p>{message}</p>
      </div>
    </section>
  );
}

function resolvePostLoginPath(status: ReturnType<typeof useSubscription>["status"]): string {
  if (status?.has_active_subscription && status.app_access_allowed) return "/dashboard";
  // Only resume payment if the user has already initiated a payment intent (i.e. they actively started paying)
  if (status?.pending_subscription?.stripe_payment_intent_id) {
    return `/plans/payment/${status.pending_subscription.id}`;
  }
  return "/plans";
}

function ProtectedRoute() {
  const { isAuthenticated } = useAuth();
  const { loading } = useSubscription();
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (loading) return <BillingLoadingScreen message="Caricamento stato workspace..." />;
  return <Outlet />;
}

function ActiveSubscriptionRoute() {
  const { status } = useSubscription();
  if (status?.has_active_subscription && status.app_access_allowed) return <Outlet />;
  // Resume payment only if the user has already initiated a payment intent
  if (status?.pending_subscription?.stripe_payment_intent_id) {
    return <Navigate to={`/plans/payment/${status.pending_subscription.id}`} replace />;
  }
  return <Navigate to="/plans" replace />;
}

function AdminRoute() {
  const { isAuthenticated, isSuperadmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (!isSuperadmin) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function ConsultantRoute() {
  const { isAuthenticated, isConsultant } = useAuth();
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (!isConsultant) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function AuthEntryRoute() {
  const { isAuthenticated, isSuperadmin, isConsultant, tokens } = useAuth();
  const { status, loading } = useSubscription();

  if (!isAuthenticated) return <AuthPage />;
  if (isSuperadmin && !status?.workspace_id) return <Navigate to="/admin" replace />;
  if (isConsultant && !tokens?.workspace_id) return <Navigate to="/studio" replace />;
  if (loading) return <BillingLoadingScreen message="Caricamento stato account..." />;
  return <Navigate to={resolvePostLoginPath(status)} replace />;
}

function FallbackRoute() {
  const { isAuthenticated } = useAuth();
  const { status, loading } = useSubscription();

  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (loading) return <BillingLoadingScreen message="Caricamento..." />;
  return <Navigate to={resolvePostLoginPath(status)} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/auth" element={<AuthEntryRoute />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/plans/payment/:subscriptionId" element={<PlanPaymentPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route element={<ActiveSubscriptionRoute />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/invoices/:invoiceId/conversations" element={<InvoiceConversationsPage />} />
              <Route path="/conversations" element={<ConversationsPage />} />
              <Route path="/policies" element={<PoliciesPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/logs" element={<LogsPage />} />
              <Route
                path="/integrations"
                element={INTEGRATIONS_ENABLED ? <IntegrationsPage /> : <Navigate to="/dashboard" replace />}
              />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/privacy" element={<SettingsPrivacyPage />} />
            </Route>
          </Route>
        </Route>

        <Route element={<AdminRoute />}>
          <Route element={<AdminShell />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/workspaces" element={<AdminWorkspacesPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/conversations" element={<AdminConversationsPage />} />
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/admin/audit" element={<AdminAuditPage />} />
            <Route path="/admin/contacts" element={<AdminContactsPage />} />
            <Route path="/admin/appointments" element={<AdminAppointmentsPage />} />
            <Route path="/admin/workspace-health" element={<AdminWorkspaceHealthPage />} />
          </Route>
        </Route>

        <Route element={<ConsultantRoute />}>
          <Route element={<StudioShell />}>
            <Route path="/studio" element={<StudioDashboardPage />} />
            <Route path="/studio/invite" element={<StudioInvitePage />} />
            <Route path="/studio/settings" element={<StudioSettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<FallbackRoute />} />
      </Routes>
    </Suspense>
  );
}
